import { format } from "@repo/shared/helpers/format";
import type { ColumnFormat } from "@repo/shared/types/column-format";

/** Format used for the share suffix appended to each pie arc label. */
const SHARE_FORMAT: ColumnFormat = {
  kind: "PERCENT",
  scale: "0-1",
  decimals: 1,
};

/** Fallback when the angle column has no user-configured format. */
const DEFAULT_VALUE_FORMAT: ColumnFormat = { kind: "NUMBER" };

/**
 * Builds the AG Charts `calloutLabel.formatter` for a pie/donut series, which
 * renders each slice as its value plus its share of the total — `1.2m (34%)`.
 *
 * The callout label carries the value rather than the sector label because AG
 * hides a sector label unless its whole text box fits inside the wedge; the
 * callout renderer draws unconditionally. Both paths receive the same formatter
 * params, so this is agnostic to which one calls it.
 *
 * The share is computed against the sum of the rendered rows, so a bucketed
 * "Other" slice reports the combined share of everything it absorbed. A
 * non-positive total (empty or all-zero data) drops the share and emits the
 * value alone, since every slice would otherwise read as `0%` or `NaN%`.
 *
 * @param data The chart's rows, as returned by the pie chart rift.
 * @param angleResultKey Result column holding the slice value (`"angle"` once
 *   the rift has aliased it, otherwise the raw `graphConfig.angleKey`).
 * @param valueFormat The user's configured format for that column.
 */
export const buildArcValueLabelFormatter = (
  data: Record<string, unknown>[],
  angleResultKey: string,
  valueFormat: ColumnFormat | undefined,
) => {
  const total = data.reduce((sum, row) => {
    const value = Number(row[angleResultKey]);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);

  const resolvedFormat = valueFormat ?? DEFAULT_VALUE_FORMAT;

  return (params: { datum?: Record<string, unknown> }): string => {
    const raw = params.datum?.[angleResultKey];
    const formattedValue = format({
      value: raw,
      format: resolvedFormat,
      style: "short",
    });

    const value = Number(raw);
    if (total <= 0 || !Number.isFinite(value)) return formattedValue;

    const share = format({
      value: value / total,
      format: SHARE_FORMAT,
      style: "short",
    });

    return `${formattedValue} (${share})`;
  };
};
